import { useEffect, useMemo } from 'react';
import { useReactiveVar } from '@apollo/client';
import { useHistory } from 'react-router-dom';

import { paletteState, userStateVar } from 'core/state';
import { isEditableTarget } from 'core/utils/isEditableTarget';
import { armGoTo, consumeGoTo, isGoToArmed } from 'core/utils/goToSequence';
import {
  visibleFor,
  STUDENT_ROLES,
  EDUCATION_ROLES,
  FINANCE_VIEW_ROLES,
  ZOOM_ROLES,
  SURVEY_ROLES,
  CAREER_ROLES,
  ADMIN_ROLES,
  DOFIN_ROLES,
} from 'core/hooks/Palette/paletteRoles';
import { PATHS } from 'router/config';
import { UserRole } from 'types';

/** One `g → <key>` destination. `allowed` reuses the palette's role sets so a
 *  chord never navigates somewhere the operator has no route to. */
export interface IGoToTarget {
  /** The second key pressed after `g`. */
  secondKey: string;
  path: PATHS;
  /** i18n leaf under `commandPalette.goto.*` (used by the cheat sheet). */
  labelKey: string;
  allowed?: UserRole[];
}

/**
 * The `g → section` map. Each `path` is a verified `PATHS` key; each role set is
 * shared with the palette's `commands.ts` so navigation and the palette agree on
 * who may reach a section.
 */
export const GOTO_TARGETS: IGoToTarget[] = [
  {
    secondKey: 'd',
    path: PATHS.dashboard,
    labelKey: 'commandPalette.goto.dashboard',
    allowed: STUDENT_ROLES,
  },
  {
    secondKey: 's',
    path: PATHS.students,
    labelKey: 'commandPalette.goto.students',
    allowed: STUDENT_ROLES,
  },
  {
    secondKey: 'g',
    path: PATHS.settingsGroups,
    labelKey: 'commandPalette.goto.groups',
    allowed: EDUCATION_ROLES,
  },
  {
    secondKey: 'c',
    path: PATHS.settingsCourses,
    labelKey: 'commandPalette.goto.courses',
    allowed: EDUCATION_ROLES,
  },
  {
    secondKey: 'f',
    path: PATHS.financesDashboard,
    labelKey: 'commandPalette.goto.finances',
    allowed: FINANCE_VIEW_ROLES,
  },
  {
    secondKey: 't',
    path: PATHS.teachers,
    labelKey: 'commandPalette.goto.teachers',
    allowed: EDUCATION_ROLES,
  },
  {
    secondKey: 'j',
    path: PATHS.settingsJournals,
    labelKey: 'commandPalette.goto.journals',
    allowed: EDUCATION_ROLES,
  },
  {
    secondKey: 'z',
    path: PATHS.zoom,
    labelKey: 'commandPalette.goto.zoom',
    allowed: ZOOM_ROLES,
  },
  {
    secondKey: 'o',
    path: PATHS.surveys,
    labelKey: 'commandPalette.goto.surveys',
    allowed: SURVEY_ROLES,
  },
  {
    // `r` for карьера/kariera — `c` is taken by courses.
    secondKey: 'r',
    path: PATHS.careerCenter,
    labelKey: 'commandPalette.goto.careerCenter',
    allowed: CAREER_ROLES,
  },
  {
    secondKey: 'u',
    path: PATHS.users,
    labelKey: 'commandPalette.goto.users',
    allowed: ADMIN_ROLES,
  },
  {
    secondKey: 'b',
    path: PATHS.debtors,
    labelKey: 'commandPalette.goto.debtors',
    allowed: STUDENT_ROLES,
  },
  {
    secondKey: 'k',
    path: PATHS.communicator,
    labelKey: 'commandPalette.goto.communicator',
    allowed: STUDENT_ROLES,
  },
  {
    // `n` — dofiNansowanie: `d` занят дашбордом, `f` финансами.
    secondKey: 'n',
    path: PATHS.dofinansowanie,
    labelKey: 'commandPalette.goto.dofinansowanie',
    allowed: DOFIN_ROLES,
  },
];

/**
 * Sequential `g → section` navigation. Pressing `g` arms a ~1.2s window (shared
 * via `goToSequence` so page shortcuts stand down); the next key navigates via
 * `history.push`. Ignored while typing in a field, while a modifier is held, or
 * while the palette/cheat sheet is open. Each destination is role-gated.
 */
export const useGoToKeys = (): void => {
  const history = useHistory();
  const userState = useReactiveVar(userStateVar);
  const roles = useMemo(() => (userState.me?.roles ?? []) as UserRole[], [
    userState,
  ]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) {
        return;
      }
      if (isEditableTarget(event.target) || paletteState().open) {
        return;
      }

      const key = event.key.toLowerCase();

      // Second key of an armed chord: navigate (or cancel on a non-target key).
      if (isGoToArmed()) {
        consumeGoTo();
        const target = GOTO_TARGETS.find((entry) => entry.secondKey === key);
        if (target && visibleFor(roles, target.allowed)) {
          event.preventDefault();
          history.push(target.path);
        }
        return;
      }

      // Arm on `g`; any other key resets a stale window.
      if (key === 'g') {
        armGoTo();
      } else {
        consumeGoTo();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [history, roles]);
};
